import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/shared/api/client'
import { useAuthStore } from '@/shared/stores/auth.store'
import type { ChartFilters, GlobalSearchResult } from './use-global-search'
import { useSearchStore } from '../stores/search.store'
import type { ChartBucketKey } from '@/features/workspace/utils/status-map'

/** An identifier field, as the options endpoint lists them. */
export type IdField = 'claim_id' | 'patient_id' | 'med_id'
export type ChartIdOperator = 'exact' | 'prefix'

export const FIELD_LABELS: Record<IdField, string> = {
  claim_id: 'Claim ID',
  patient_id: 'Patient ID',
  med_id: 'Med ID',
}

export interface ChartIdCondition {
  field: IdField
  operator: ChartIdOperator
  values: string[]
}

export interface ChartIdSearchRequest {
  conditions: ChartIdCondition[]
  filters: ChartFilters
  page: number
  page_size: number
}

export interface ChartIdOptions {
  fields: { field: IdField; operators: ChartIdOperator[] }[]
  facilities: { id: string; name: string; is_active: boolean }[]
  /** Status categories this user may filter on, in tab order (role-gated queue tabs + History). */
  buckets: ChartBucketKey[]
  max_values: number
}

interface ChartIdResponse {
  data: Omit<GlobalSearchResult, 'category'>[]
  page: number
  page_size: number
  total: number
  summary: { field: IdField; searched: number; matched: number; unmatched: string[] }[]
}

/** Below this, an encrypted prefix search is refused (backend PREFIX_MIN_LEN). */
export const PREFIX_MIN_LEN = 3
export const CHART_ID_PAGE_SIZE = 25

/**
 * Who is asking — the user, the active client and (while impersonating) the real
 * actor. The backend scopes the options and every search result to this, so
 * every cached search query is keyed on it: a super admin's cached options (Med
 * ID only) must never be served to the client admin they then impersonate.
 */
export function useSearchIdentity(): string {
  return useAuthStore((s) => {
    const u = s.user
    return u ? `${u.id}|${u.active_client?.id ?? ''}|${u.is_impersonating ? u.real_actor?.id ?? 'imp' : ''}` : ''
  })
}

export const CHART_ID_OPTIONS_KEY = 'chart-id-search-options'

/**
 * What this user may search and filter on — decided by the backend, per request,
 * from the caller's live unmask grants. staleTime 0: every panel open (the query
 * is enabled only while the panel is open) refetches, so a grant approved since
 * the last open — by someone else, in another session — shows its Claim ID /
 * Patient ID fields; a revoked or expired one drops them. The cached list still
 * renders meanwhile, and the field clamp (`effectiveField`) plus the backend's
 * own check keep a stale field from ever being sent. No grant signature in the
 * key: the client holds no grantee-scoped grant list (`/access/grants` lists
 * every grant to a super admin and loads only on the Access pages).
 */
export const chartIdOptionsQuery = (identity: string) => ({
  queryKey: [CHART_ID_OPTIONS_KEY, identity],
  queryFn: () => api.get<{ data: ChartIdOptions }>('/search/advanced/options').then((r) => r.data.data),
  staleTime: 0,
})

export function useChartIdOptions(enabled = true) {
  const identity = useSearchIdentity()
  return useQuery({ ...chartIdOptionsQuery(identity), enabled })
}

/** The bulk field to use: the chosen one if this user may search it, else Claim ID, else the first allowed. */
export function effectiveField(chosen: IdField | null, allowed: IdField[]): IdField | undefined {
  if (chosen && allowed.includes(chosen)) return chosen
  return allowed.includes('claim_id') ? 'claim_id' : allowed[0]
}

/** The backend refused a field this identity may not search (advanced-search.service.ts, ValidationError). */
export const isFieldRejected = (err: unknown) => {
  const r = (err as { response?: { status?: number; data?: { error?: { message?: string } } } } | null)?.response
  return r?.status === 400 && /search fields are not available/i.test(r.data?.error?.message ?? '')
}

/**
 * Facility / Status chips → filter params, shared by quick search and Advanced
 * Search. Status categories go as their tab keys; the backend applies each
 * tab's own predicate (config/buckets.ts).
 * - Every facility option selected → no `facility_ids` (the scope already bounds
 *   the result; keeps the URL under the header limit at ~420 ids).
 */
export function chipFilters(
  facilityIds: string[],
  buckets: ChartBucketKey[],
  options?: Pick<ChartIdOptions, 'facilities'>,
): ChartFilters {
  const all = options?.facilities ?? []
  const sel = new Set(facilityIds)
  const everyFacility = all.length > 0 && sel.size === all.length && all.every((f) => sel.has(f.id))
  return {
    facility_ids: facilityIds.length && !everyFacility ? facilityIds : undefined,
    buckets: buckets.length ? buckets : undefined,
  }
}

/** The search store's Facility / Status chips as quick-search chart filters (null = no filter). */
export function useChartFilters(canFilter: boolean): ChartFilters | null {
  const facilityIds = useSearchStore((s) => s.facilityIds)
  const buckets = useSearchStore((s) => s.buckets)
  const active = canFilter && (facilityIds.length > 0 || buckets.length > 0)
  const options = useChartIdOptions(active).data
  return useMemo(
    () => (active ? chipFilters(facilityIds, buckets, options) : null),
    [active, facilityIds, buckets, options],
  )
}

/**
 * Runs a chart-identifier search (a pasted list of IDs). `onFieldRejected`: the
 * backend refused the field for this identity (see `isFieldRejected`).
 * Identifiers go in the POST body, never the URL. A superseded request is
 * aborted through `signal`. gcTime 0: the identifiers are part of the key, so
 * drop them from the cache as soon as nothing renders them.
 */
export function useChartIdSearch(request: ChartIdSearchRequest | null, onFieldRejected?: () => void) {
  return useQuery({
    queryKey: ['chart-id-search', request],
    queryFn: ({ signal }) =>
      api.post<{ data: ChartIdResponse }>('/search/advanced', request, { signal }).then(
        (r) => r.data.data,
        (err: unknown) => {
          if (isFieldRejected(err)) onFieldRejected?.()
          throw err
        },
      ).then((d) => {
        return {
          ...d,
          results: d.data.map<GlobalSearchResult>((item) => ({
            ...item,
            id: `encounter-${item.id}`,
            category: 'encounter',
          })),
        }
      }),
    enabled: request !== null,
    gcTime: 0,
    retry: false,
    placeholderData: (prev) => prev,
  })
}
